import { tC } from "../../utils/TranslateWithContentLanguage";
import {
  LayoutGrid,
  RefreshCw,
  Target,
  BookOpen,
  Award,
  Users,
  CheckCircle,
  FileOutput,
  Globe,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BookMarked,
  Info,
  ListChecks,
  Copy,
  Check,
  BookmarkIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import CardImage from "../tool_components/CardImage";
import AiSupportedTasksCardPdf from "./AiSupportedTasksCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import { Button, Text } from "@core/index";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import useImagePdf from "../pdf/useImagePdf";
import { AiSupportedTasksResponseDto } from "@packages/api/madlenAPI.schemas";
import { motion, AnimatePresence } from "framer-motion";
import { MascotBubble } from "../tool_components/MascotBubble";
import {
  getGetAiSupportedTasksQueryKey,
  getGetUserAiSupportedTasksQueryKey,
  updateAiSupportedTasks,
} from "@packages/api/madlenAPI";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";
import useIsAuthor from "@packages/hooks/useIsAuthor";
import { useAppSelector } from "@packages/store/hooks";
import { useQueryClient } from "@tanstack/react-query";
import EditableMarkdown from "../tool_components/EditableMarkdown";

type TabType =
  | "description"
  | "stepsAndResearch"
  | "expectedOutput"
  | "rubric"
  | "notes";

export interface AiSupportedTasksCardProps {
  tool: AiSupportedTasksResponseDto;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  isEditable?: boolean;
}

export const AiSupportedTasksCard = ({
  tool,
  isImageLoading = false,
  onCreateAgain,
  isEditable = false,
}: AiSupportedTasksCardProps) => {
  const finalImageUrl = useImagePdf(tool.imageUrl);
  const contentLanguage = tool.contentLanguage || "tr";
  const [activeTab, setActiveTab] = useState<TabType>("description");
  const contentRef = useRef<HTMLDivElement>(null);
  const [expandedSteps, setExpandedSteps] = useState<number[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>(["main"]);
  const [copiedPrompts, setCopiedPrompts] = useState<Record<string, boolean>>(
    {},
  );
  const isAuthor = useIsAuthor(tool.authorId);
  const queryClient = useQueryClient();
  // Track expanded rubrics (expanded by default)
  const [expandedRubrics, setExpandedRubrics] = useState<number[]>([]);
  // State for PDF customization modal
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  // Animation variants for smoother transitions
  const tabContentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  // Initialize expanded steps when tab changes or component mounts
  useEffect(() => {
    if (activeTab === "stepsAndResearch" && tool.steps) {
      // Expand all steps by default
      const allStepIds = tool.steps
        .map((step) => step.id || 0)
        .filter((id) => id !== 0);
      setExpandedSteps(allStepIds);
    }

    // Initialize all rubrics as expanded by default when rubric tab is active
    if (activeTab === "rubric" && tool.assessmentRubric) {
      const allRubricIds = tool.assessmentRubric.map((_, index) => index);
      setExpandedRubrics(allRubricIds);
    }
  }, [activeTab, tool.steps, tool.assessmentRubric]);

  // Toggle step expansion with debounce to prevent animation issues
  const toggleStep = (id: number) => {
    setExpandedSteps((prev) =>
      prev.includes(id)
        ? prev.filter((stepId) => stepId !== id)
        : [...prev, id],
    );
  };

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section],
    );
  };

  // Toggle rubric expansion
  const toggleRubric = (index: number) => {
    setExpandedRubrics((prev) =>
      prev.includes(index)
        ? prev.filter((id) => id !== index)
        : [...prev, index],
    );
  };

  // Copy prompt to clipboard
  const copyPrompt = (prompt: string, stepId: number, promptIndex: number) => {
    navigator.clipboard.writeText(prompt).then(() => {
      // Set copied state for this specific prompt
      const key = `${stepId}-${promptIndex}`;
      setCopiedPrompts((prev) => ({ ...prev, [key]: true }));

      // Reset after 2 seconds
      setTimeout(() => {
        setCopiedPrompts((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    });
  };

  // Scroll to content when tab changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeTab]);

  // Get available tabs based on data
  const getAvailableTabs = () => {
    const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
      {
        id: "description",
        label: tC(contentLanguage, "common.description"),
        icon: <BookOpen className="w-5 h-5" />,
      },
      {
        id: "stepsAndResearch",
        label: tC(contentLanguage, "common.steps"),
        icon: <ListChecks className="w-5 h-5" />,
      },
    ];

    if (tool.expectedOutput) {
      tabs.push({
        id: "expectedOutput",
        label: tC(contentLanguage, "common.expectedOutput"),
        icon: <FileOutput className="w-5 h-5" />,
      });
    }

    if (tool.notes) {
      tabs.push({
        id: "notes",
        label: tC(contentLanguage, "common.notes"),
        icon: <Users className="w-5 h-5" />,
      });
    }

    if (tool.assessmentRubric && tool.assessmentRubric.length > 0) {
      tabs.push({
        id: "rubric",
        label: tC(contentLanguage, "common.assessmentRubric"),
        icon: <Award className="w-5 h-5" />,
      });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  const handleSaveDescription = async (newValue: string) => {
    try {
      await updateAiSupportedTasks(tool.id, {
        description: newValue,
      });

      // Invalidate the necessary queries to refresh the data
      await queryClient.invalidateQueries({
        queryKey: getGetUserAiSupportedTasksQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetAiSupportedTasksQueryKey(tool.id),
      });
    } catch (error) {
      console.error("Error updating AI Supported Task description:", error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "description":
        return (
          <motion.div
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            {/* Main Description Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/40">
                  <BookOpen className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <Text
                  variant="h5"
                  className="text-green-700 dark:text-green-300"
                >
                  {tC(contentLanguage, "common.description")}
                </Text>
              </div>
              <div className="md:p-5 p-4">
                <div className="prose dark:prose-invert max-w-none prose-green prose-headings:text-green-700 dark:prose-headings:text-green-300">
                  <EditableMarkdown
                    value={tool.description ?? ""}
                    isEditable={isEditable}
                    handleSave={handleSaveDescription}
                  />
                </div>
              </div>
            </div>

            {/* Learning Objectives Card */}
            {tool.learningObjectives && tool.learningObjectives.length > 0 && (
              <motion.div
                initial={{ opacity: 0.9, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                    <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <Text
                    variant="h5"
                    className="text-blue-700 dark:text-blue-300"
                  >
                    {tC(contentLanguage, "common.learningObjectives")}
                  </Text>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 gap-3">
                    {tool.learningObjectives.map((objective, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index, duration: 0.4 }}
                        className="flex items-start group"
                      >
                        <div className="flex-shrink-0 mr-3">
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium text-sm group-hover:scale-110 transition-transform duration-200">
                            {index + 1}
                          </div>
                        </div>
                        <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-lg p-3 border border-blue-100 dark:border-blue-800/30 hover:shadow-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 flex-1">
                          <Text
                            variant="p"
                            className="text-gray-700 dark:text-gray-300"
                          >
                            {objective}
                          </Text>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Benefits Section */}
            {tool.benefits && tool.benefits.length > 0 && (
              <motion.div
                initial={{ opacity: 0.9, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                    <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <Text
                    variant="h5"
                    className="text-emerald-700 dark:text-emerald-300"
                  >
                    {tC(contentLanguage, "common.benefits")}
                  </Text>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tool.benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * index, duration: 0.4 }}
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                        }}
                        className="group bg-gradient-to-br from-white to-emerald-50/30 dark:from-gray-800 dark:to-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800/30 p-4 hover:border-emerald-200 dark:hover:border-emerald-700/40 transition-all duration-200"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 font-medium text-sm shadow-inner flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                            {index + 1}
                          </div>
                          <Text
                            variant="p"
                            className="text-gray-700 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-200"
                          >
                            {benefit}
                          </Text>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        );

      case "stepsAndResearch":
        return (
          <motion.div
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            {/* Recommended AI Platform */}
            {tool.recommendedAiPlatform && (
              <motion.div
                initial={{ opacity: 0.9, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
                    <Globe className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex flex-col">
                    <Text
                      variant="h5"
                      className="text-purple-700 dark:text-purple-300"
                    >
                      {tool.recommendedAiPlatform.title}
                    </Text>
                    <Text
                      variant="p"
                      className="text-purple-600/80 dark:text-purple-400/80 text-sm"
                    >
                      {tC(contentLanguage, "common.recommendedPlatform")}
                    </Text>
                  </div>
                  <div className="ml-auto">
                    <a
                      href={tool.recommendedAiPlatform.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium text-sm transition-colors"
                    >
                      {tC(contentLanguage, "common.visitPlatform")}{" "}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
                <div className="p-5">
                  <Text
                    variant="p"
                    className="text-gray-700 dark:text-gray-300"
                  >
                    {tool.recommendedAiPlatform.description}
                  </Text>
                </div>
              </motion.div>
            )}

            {/* Research Process */}
            {tool.researchProcessDescription && (
              <motion.div
                initial={{ opacity: 0.9, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <Text
                    variant="h5"
                    className="text-blue-700 dark:text-blue-300"
                  >
                    {tC(contentLanguage, "common.researchProcess")}
                  </Text>
                </div>
                <div className="p-5">
                  <div className="prose dark:prose-invert max-w-none prose-blue prose-headings:text-blue-700 dark:prose-headings:text-blue-300">
                    <EditableMarkdown
                      value={tool.researchProcessDescription ?? ""}
                      isEditable={isEditable}
                      handleSave={async (newValue) => {
                        await updateAiSupportedTasks(tool.id, {
                          researchProcessDescription: newValue,
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetUserAiSupportedTasksQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetAiSupportedTasksQueryKey(tool.id),
                        });
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Steps - Improved UI */}
            <motion.div
              initial={{ opacity: 0.9, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/40">
                  <ListChecks className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <Text
                  variant="h5"
                  className="text-green-700 dark:text-green-300"
                >
                  {tC(contentLanguage, "common.steps")}
                </Text>
              </div>
              <div className="p-5">
                <div className="relative pl-4 border-l-2 border-green-300 dark:border-green-700">
                  {tool.steps?.map((step, index) => (
                    <div key={step.id} className="mb-6 last:mb-0">
                      <div className="absolute left-0 -translate-x-[9px] w-4 h-4 rounded-full bg-green-500 dark:bg-green-600 border-2 border-white dark:border-gray-900"></div>
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                        <div
                          className="flex justify-between items-center p-4 cursor-pointer bg-gradient-to-r from-green-50 to-white dark:from-green-900/30 dark:to-gray-800 hover:bg-green-50 dark:hover:bg-green-900/40 transition-colors"
                          onClick={() => toggleStep(step.id || 0)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 font-medium text-sm shadow-inner">
                              {step.id}
                            </div>
                            <Text
                              variant="h6"
                              className="text-base font-semibold text-green-700 dark:text-green-300"
                            >
                              {step.title}
                            </Text>
                          </div>
                          {expandedSteps.includes(step.id || 0) ? (
                            <ChevronUp className="h-5 w-5 text-green-500 dark:text-green-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-green-500 dark:text-green-400" />
                          )}
                        </div>
                        <AnimatePresence>
                          {expandedSteps.includes(step.id || 0) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="p-5 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <EditableMarkdown
                                  value={step.description ?? ""}
                                  isEditable={isEditable}
                                  handleSave={async (newValue) => {
                                    // Create a new steps array with the updated description
                                    const updatedSteps = tool.steps?.map((s) =>
                                      s.id === step.id
                                        ? { ...s, description: newValue }
                                        : s,
                                    );

                                    await updateAiSupportedTasks(tool.id, {
                                      steps: updatedSteps,
                                    });

                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetUserAiSupportedTasksQueryKey(),
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: getGetAiSupportedTasksQueryKey(
                                        tool.id,
                                      ),
                                    });
                                  }}
                                />
                                {step.examplePrompts &&
                                  step.examplePrompts.length > 0 && (
                                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                      <Text
                                        variant="h6"
                                        className="text-base font-semibold text-green-700 dark:text-green-300 mb-3"
                                      >
                                        {tC(
                                          contentLanguage,
                                          "common.examplePrompts",
                                        )}
                                      </Text>
                                      <div className="space-y-4">
                                        {step.examplePrompts.map(
                                          (prompt, idx) => {
                                            const promptKey = `${step.id}-${idx}`;
                                            const isCopied =
                                              copiedPrompts[promptKey];

                                            return (
                                              <div
                                                key={idx}
                                                className="relative bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden"
                                              >
                                                <div className="absolute top-2 right-2 z-10">
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      copyPrompt(
                                                        prompt,
                                                        step.id || 0,
                                                        idx,
                                                      );
                                                    }}
                                                    className="flex items-center justify-center p-1.5 rounded-md bg-gray-200/70 dark:bg-gray-700/70 backdrop-blur-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                                    title={
                                                      isCopied
                                                        ? "Copied!"
                                                        : "Copy to clipboard"
                                                    }
                                                  >
                                                    {isCopied ? (
                                                      <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                                                    ) : (
                                                      <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                                    )}
                                                  </button>
                                                </div>
                                                <div className="p-4 font-mono text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                  {prompt}
                                                </div>
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        );

      case "expectedOutput":
        return (
          <motion.div
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            className="space-y-5"
          >
            {tool.expectedOutput ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <FileOutput className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <Text
                      variant="h5"
                      className="text-blue-700 dark:text-blue-300"
                    >
                      {tC(contentLanguage, "common.expectedOutput")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-blue-600/80 dark:text-blue-400/80 text-sm"
                    >
                      {tC(contentLanguage, "common.format")}:{" "}
                      <span className="font-medium">
                        {tool.expectedOutput.format}
                      </span>
                    </Text>
                  </div>
                </div>

                <div className="space-y-4 mt-2">
                  {tool.expectedOutput.sections?.map((section, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
                    >
                      <div className="bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 text-xs font-medium flex-shrink-0">
                            {index + 1}
                          </div>
                          <Text
                            variant="span"
                            className="font-medium text-blue-700 dark:text-blue-300"
                          >
                            {section.title}
                          </Text>
                        </div>
                        {section.wordCount ? (
                          <Text
                            variant="p"
                            className="text-blue-500/80 dark:text-blue-400/70 text-sm px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800/30"
                          >
                            {section.wordCount}{" "}
                            {tC(contentLanguage, "common.words")}
                          </Text>
                        ) : null}
                      </div>
                      <div className="p-4 bg-white dark:bg-gray-800">
                        <EditableMarkdown
                          value={section.description ?? ""}
                          isEditable={isEditable}
                          handleSave={async (newValue) => {
                            // Create a new sections array with the updated description
                            const updatedSections =
                              tool.expectedOutput?.sections?.map((s, i) =>
                                i === index
                                  ? { ...s, description: newValue }
                                  : s,
                              );

                            await updateAiSupportedTasks(tool.id, {
                              expectedOutput: {
                                ...tool.expectedOutput,
                                sections: updatedSections,
                              },
                            });

                            await queryClient.invalidateQueries({
                              queryKey: getGetUserAiSupportedTasksQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetAiSupportedTasksQueryKey(tool.id),
                            });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Text variant="p" className="text-gray-500 dark:text-gray-400">
                {tC(contentLanguage, "aiSupportedTasks.empty.noExpectedOutput")}
              </Text>
            )}
          </motion.div>
        );

      case "notes":
        return (
          <motion.div
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {tool.notes && (
              <>
                {tool.notes.forTeachers &&
                  tool.notes.forTeachers.length > 0 && (
                    <div className="p-5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                        <div>
                          <Text
                            variant="h6"
                            className="text-blue-800 dark:text-blue-300 mb-3"
                          >
                            {tC(contentLanguage, "common.forTeachers")}
                          </Text>
                          <ul className="space-y-2 list-disc list-inside text-blue-700 dark:text-blue-300/90">
                            {tool.notes.forTeachers.map((note, index) => (
                              <li key={index}>
                                <EditableMarkdown
                                  value={note ?? ""}
                                  isEditable={isEditable}
                                  handleSave={async (newValue) => {
                                    // Create a new forTeachers array with the updated note
                                    const updatedForTeachers =
                                      tool.notes?.forTeachers?.map((n, i) =>
                                        i === index ? newValue : n,
                                      );

                                    await updateAiSupportedTasks(tool.id, {
                                      notes: {
                                        ...tool.notes,
                                        forTeachers: updatedForTeachers,
                                      },
                                    });

                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetUserAiSupportedTasksQueryKey(),
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: getGetAiSupportedTasksQueryKey(
                                        tool.id,
                                      ),
                                    });
                                  }}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                {tool.notes.forStudents &&
                  tool.notes.forStudents.length > 0 && (
                    <div className="p-5 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
                      <div className="flex items-start gap-3">
                        <BookMarked className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                        <div>
                          <Text
                            variant="h6"
                            className="text-purple-800 dark:text-purple-300 mb-3"
                          >
                            {tC(contentLanguage, "common.forStudents")}
                          </Text>
                          <ul className="space-y-2 list-disc list-inside text-purple-700 dark:text-purple-300/90">
                            {tool.notes.forStudents.map((note, index) => (
                              <li key={index}>
                                <EditableMarkdown
                                  value={note ?? ""}
                                  isEditable={isEditable}
                                  handleSave={async (newValue) => {
                                    // Create a new forStudents array with the updated note
                                    const updatedForStudents =
                                      tool.notes?.forStudents?.map((n, i) =>
                                        i === index ? newValue : n,
                                      );

                                    await updateAiSupportedTasks(tool.id, {
                                      notes: {
                                        ...tool.notes,
                                        forStudents: updatedForStudents,
                                      },
                                    });

                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetUserAiSupportedTasksQueryKey(),
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: getGetAiSupportedTasksQueryKey(
                                        tool.id,
                                      ),
                                    });
                                  }}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Ethical Usage Tips */}
                <div className="p-5 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20">
                  <div className="flex items-start gap-3">
                    <BookmarkIcon className="w-5 h-5 text-teal-600 dark:text-teal-400 shrink-0 mt-0.5" />
                    <div>
                      <Text
                        variant="h6"
                        className="text-teal-800 dark:text-teal-300 mb-3"
                      >
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.ethicalUsageTips",
                        )}
                      </Text>
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                          </div>
                          <p className="text-teal-700 dark:text-teal-300/90">
                            {tC(
                              contentLanguage,
                              "bloomsTaxonomyActivity.ethicTip1",
                            )}
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                          </div>
                          <p className="text-teal-700 dark:text-teal-300/90">
                            {tC(
                              contentLanguage,
                              "bloomsTaxonomyActivity.ethicTip2",
                            )}
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                          </div>
                          <p className="text-teal-700 dark:text-teal-300/90">
                            {tC(
                              contentLanguage,
                              "bloomsTaxonomyActivity.ethicTip3",
                            )}
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                          </div>
                          <p className="text-teal-700 dark:text-teal-300/90">
                            {tC(
                              contentLanguage,
                              "bloomsTaxonomyActivity.ethicTip4",
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        );

      case "rubric":
        return (
          <motion.div
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {tool.assessmentRubric && tool.assessmentRubric.length > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                    <Award className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <Text
                    variant="h5"
                    className="text-green-700 dark:text-green-300"
                  >
                    {tC(contentLanguage, "common.assessmentRubric")}
                  </Text>
                </div>

                {tool.assessmentRubric.map((rubric, index) => (
                  <div
                    key={index}
                    className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl border border-green-100/50 dark:border-green-700/30 transition-all duration-200 shadow-sm hover:shadow-lg"
                  >
                    <div
                      className="p-4 border-b border-green-100 dark:border-green-800/30 cursor-pointer"
                      onClick={() => toggleRubric(index)}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h5 className="font-semibold text-lg text-green-700 dark:text-green-300 flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          {rubric.name}
                        </h5>
                        <div className="flex items-center">
                          {expandedRubrics.includes(index) ? (
                            <ChevronUp className="h-5 w-5 text-green-500 dark:text-green-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-green-500 dark:text-green-400" />
                          )}
                        </div>
                      </div>
                      {rubric.description && (
                        <div className="mt-3 text-gray-600 dark:text-gray-300 text-sm ml-8 border-l-2 border-green-200 dark:border-green-800/50 pl-3">
                          <EditableMarkdown
                            value={rubric.description ?? ""}
                            isEditable={isEditable}
                            handleSave={async (newValue) => {
                              // Create a new assessment rubric array with the updated description
                              const updatedRubrics = tool.assessmentRubric?.map(
                                (r, i) =>
                                  i === index
                                    ? { ...r, description: newValue }
                                    : r,
                              );

                              await updateAiSupportedTasks(tool.id, {
                                assessmentRubric: updatedRubrics,
                              });

                              await queryClient.invalidateQueries({
                                queryKey: getGetUserAiSupportedTasksQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetAiSupportedTasksQueryKey(
                                  tool.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <AnimatePresence initial={false}>
                      {expandedRubrics.includes(index) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="p-4">
                            <h6 className="text-sm text-green-500 dark:text-green-400 mb-3 uppercase tracking-wide font-medium">
                              {tC(
                                contentLanguage,
                                "aiSupportedTasks.performanceLevels",
                              )}
                            </h6>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                              {rubric.levels
                                ?.sort(
                                  (a, b) => (b.score || 0) - (a.score || 0),
                                )
                                .map((level, levelIdx) => {
                                  // Determine colors and labels based on score (3, 2, 1)
                                  let levelColor, indicatorColor, labelText;

                                  // Extract level name from description if it follows the pattern "LevelName - Description"
                                  let levelDetail = level.description || "";
                                  const dashIndex = levelDetail.indexOf(" - ");
                                  if (dashIndex > 0) {
                                    levelDetail = levelDetail.substring(
                                      dashIndex + 3,
                                    ); // Skip the " - " part
                                  }

                                  const score = level.score || 0;
                                  if (score === 4) {
                                    levelColor =
                                      "bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-900/10 dark:border-emerald-800/30";
                                    indicatorColor = "bg-emerald-500";
                                    labelText = tC(
                                      contentLanguage,
                                      "aiSupportedTasks.excellent",
                                    );
                                  } else if (score === 3) {
                                    levelColor =
                                      "bg-green-50/70 border-green-200/70 dark:bg-green-900/10 dark:border-green-800/30";
                                    indicatorColor = "bg-green-500";
                                    labelText = tC(
                                      contentLanguage,
                                      "aiSupportedTasks.good",
                                    );
                                  } else if (score === 2) {
                                    levelColor =
                                      "bg-amber-50/70 border-amber-100/70 dark:bg-amber-900/10 dark:border-amber-800/30";
                                    indicatorColor = "bg-amber-500";
                                    labelText = tC(
                                      contentLanguage,
                                      "aiSupportedTasks.satisfactory",
                                    );
                                  } else {
                                    levelColor =
                                      "bg-red-50/70 border-red-100/70 dark:bg-red-900/10 dark:border-red-800/30";
                                    indicatorColor = "bg-red-500";
                                    labelText = tC(
                                      contentLanguage,
                                      "aiSupportedTasks.needsImprovements",
                                    );
                                  }

                                  return (
                                    <div
                                      key={levelIdx}
                                      className={`p-3 rounded-lg border hover:shadow-sm transition-all duration-200 ${levelColor}`}
                                    >
                                      <div className="flex items-center gap-2 pb-1.5 mb-1.5 border-b border-green-100 dark:border-green-800/30">
                                        <div
                                          className={`w-3 h-3 rounded-full ${indicatorColor}`}
                                        ></div>
                                        <div className="flex justify-between w-full">
                                          <h6 className="font-medium text-green-600 dark:text-green-400 text-sm">
                                            {labelText}
                                          </h6>
                                        </div>
                                      </div>
                                      <div className="prose-sm text-gray-700 dark:text-gray-300">
                                        <EditableMarkdown
                                          value={levelDetail ?? ""}
                                          isEditable={isEditable}
                                          handleSave={async (newValue) => {
                                            // Create new levels array with the updated description
                                            // We need to preserve the prefix if it exists
                                            let updatedDescription = newValue;
                                            if (dashIndex > 0) {
                                              const prefix =
                                                level.description?.substring(
                                                  0,
                                                  dashIndex + 3,
                                                ) || "";
                                              updatedDescription =
                                                prefix + newValue;
                                            }

                                            const updatedLevels =
                                              rubric.levels?.map((l, i) =>
                                                i === levelIdx
                                                  ? {
                                                      ...l,
                                                      description:
                                                        updatedDescription,
                                                    }
                                                  : l,
                                              );

                                            // Create a new assessment rubric array with the updated levels
                                            const updatedRubrics =
                                              tool.assessmentRubric?.map(
                                                (r, i) =>
                                                  i === index
                                                    ? {
                                                        ...r,
                                                        levels: updatedLevels,
                                                      }
                                                    : r,
                                              );

                                            await updateAiSupportedTasks(
                                              tool.id,
                                              {
                                                assessmentRubric:
                                                  updatedRubrics,
                                              },
                                            );

                                            await queryClient.invalidateQueries(
                                              {
                                                queryKey:
                                                  getGetUserAiSupportedTasksQueryKey(),
                                              },
                                            );
                                            await queryClient.invalidateQueries(
                                              {
                                                queryKey:
                                                  getGetAiSupportedTasksQueryKey(
                                                    tool.id,
                                                  ),
                                              },
                                            );
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            ) : (
              <Text variant="p" className="text-gray-500 dark:text-gray-400">
                {tC(
                  contentLanguage,
                  "aiSupportedTasks.empty.noAssessmentRubric",
                )}
              </Text>
            )}
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div
        data-testid="ai-supported-tasks-card"
        className="min-h-screen bg-gradient-to-b from-green-50/20 to-white dark:from-green-900/20 dark:to-gray-900"
      >
        <div className="container max-w-7xl mx-auto px-4 py-4">
          <div className="mb-7">
            <CardImage
              courseName={tool?.courseName ?? ""}
              gradeLevel={Number(tool?.gradeLevel) || 0}
              imageUrl={tool?.imageUrl}
              videoUrl={tool?.videoUrl}
              isLoading={isImageLoading}
              createdAt={tool.createdAt}
              theme="green"
              topic={
                <div className="flex md:flex-row flex-col items-center gap-4 justify-center">
                  <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                    <LayoutGrid size={24} />
                  </div>
                  <div className="text-center md:text-left">
                    <Text variant="h1" className="mb-2">
                      {tool?.title || tool?.prompt}
                    </Text>
                  </div>
                </div>
              }
              contentLanguage={contentLanguage}
            />
            <div className="flex justify-end gap-2 mt-4">
              {onCreateAgain && (
                <Button
                  onClick={onCreateAgain}
                  variant="solid"
                  size="md"
                  textColor="text-white dark:text-white"
                  bgColor="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  rounded="xl"
                >
                  <RefreshCw size={18} />
                  <span>{tC(contentLanguage, "common.createAnother")}</span>
                </Button>
              )}
              <PdfDownloadButton
                pdfTitle={tool?.title || ""}
                openCustomizationModal={() =>
                  setShowPdfCustomizationModal(true)
                }
                document={
                  <AiSupportedTasksCardPdf
                    tool={{
                      ...tool,
                      imageUrl: finalImageUrl,
                    }}
                    customizationSettings={customizationSettings}
                  />
                }
                btnTheme="bg-gradient-to-r from-green-500 to-emerald-600"
              />
            </div>
          </div>

          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-3xl shadow-xl border border-gray-100/70 dark:border-gray-700/30 overflow-hidden relative">
            {/* Regenerating overlay */}
            {tool.status === "REGENERATING" && <RegeneratingOverlay />}

            {/* Tab navigation - Styled similar to BloomTaxonomyCard */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/40 dark:to-emerald-900/40 border-b border-green-100 dark:border-green-900/50 p-3">
              <div className="flex flex-wrap justify-center gap-2 overflow-x-auto scrollbar-hide p-1">
                {availableTabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <motion.button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id as TabType);
                        contentRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                      whileHover={!isActive ? { scale: 1.05 } : {}}
                      whileTap={{ scale: 0.95 }}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300
                    ${
                      isActive
                        ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30"
                        : "bg-white/70 dark:bg-gray-800/70 text-green-600 dark:text-green-400 hover:bg-green-500/10 hover:shadow-md shadow-sm"
                    }`}
                    >
                      {tab.icon}
                      <span className="hidden md:inline">{tab.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Tab content - Using the improved inner content */}
            <div className="md:p-6 p-0 relative min-h-[50vh]">
              <div ref={contentRef} className="absolute -top-20 -z-10" />
              <AnimatePresence mode="wait">
                <div key={activeTab}>{renderTabContent()}</div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* PDF Customization Modal */}
        <PdfCustomizationModal
          show={showPdfCustomizationModal}
          onClose={() => setShowPdfCustomizationModal(false)}
          hideSchoolLogoOptions
          documentComponent={
            <AiSupportedTasksCardPdf
              tool={{
                ...tool,
                imageUrl: finalImageUrl,
              }}
              customizationSettings={customizationSettings}
            />
          }
        />
      </div>
      {isAuthor && (
        <MascotBubble
          toolQueryKey={getGetAiSupportedTasksQueryKey(tool.id)}
          toolsQueryKey={getGetUserAiSupportedTasksQueryKey()}
          createChatParams={{
            teacherToolId: tool.id,
            toolType: "AI_SUPPORTED_TASKS",
          }}
        />
      )}
    </>
  );
};

export default AiSupportedTasksCard;
